// AcceptInvitePage — Phase 16 Gate 3 — invite-token + password capture.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { auth } from "@/api/endpoints";
import { setStoredToken } from "@/api/client";
import { serverDetail } from "@/api/client";

export default function AcceptInvitePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";

    const [info, setInfo] = useState(null);
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [err, setErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loadingInfo, setLoadingInfo] = useState(true);

    useEffect(() => {
        if (!token) {
            setErr("Missing or invalid invite token.");
            setLoadingInfo(false);
            return;
        }
        auth.inviteInfo(token)
            .then((data) => { setInfo(data); setLoadingInfo(false); })
            .catch((e) => { setErr(serverDetail(e)); setLoadingInfo(false); });
    }, [token]);

    async function onSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        if (pw !== pw2) {
            setErr("Passwords do not match.");
            return;
        }
        if (pw.length < 10) {
            setErr("Password must be at least 10 characters long.");
            return;
        }
        setSubmitting(true);
        setErr(null);
        try {
            const data = await auth.acceptInvite(token, pw);
            setStoredToken(data.access_token, data.expires_at);
            // Auth context picks up the token via window.localStorage on mount; navigate.
            navigate("/run/history", { replace: true });
        } catch (apiErr) {
            setErr(serverDetail(apiErr));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="accept-invite-page">
            <section className="section" style={{ paddingTop: 80 }}>
                <div className="section__inner" style={{ maxWidth: 640 }}>
                    <span className="section__eyebrow">// INVITATION</span>
                    <h1 className="section__heading">Set your password</h1>
                    <p className="section__lede" style={{ marginBottom: 24 }}>
                        Choose a password for your UAVSimNet account. Minimum 10 characters,
                        must include a letter and a digit.
                    </p>

                    {loadingInfo && (
                        <div className="banner" data-testid="accept-invite-loading">Validating invite token…</div>
                    )}

                    {!loadingInfo && err && !info && (
                        <div className="banner banner--danger" data-testid="accept-invite-error-blocking">
                            {err}
                        </div>
                    )}

                    {info && (
                        <form className="form-card" onSubmit={onSubmit} data-testid="accept-invite-form">
                            <div className="banner banner--info" style={{ marginBottom: 24 }}>
                                <KeyRound size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
                                Account: <strong>{info.username}</strong> · Tenant: <code>{info.tenant_id}</code>
                            </div>
                            {err && (
                                <div className="banner banner--danger" style={{ marginBottom: 18 }} data-testid="accept-invite-error">
                                    {err}
                                </div>
                            )}
                            <div className="form-row">
                                <label htmlFor="pw">New password</label>
                                <input
                                    id="pw"
                                    type="password"
                                    required
                                    minLength={10}
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    data-testid="accept-invite-input-password"
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="pw2">Confirm password</label>
                                <input
                                    id="pw2"
                                    type="password"
                                    required
                                    minLength={10}
                                    value={pw2}
                                    onChange={(e) => setPw2(e.target.value)}
                                    data-testid="accept-invite-input-password-confirm"
                                />
                            </div>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={submitting}
                                style={{ width: "100%", justifyContent: "center" }}
                                data-testid="accept-invite-submit"
                            >
                                {submitting ? "Activating…" : (
                                    <>
                                        <CheckCircle2 size={16} />
                                        Activate account
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
